<script setup lang="ts">
import { explainMonsterAffix } from '@/core/monster/affix'
import { useUserStore } from '@/stores/modules/user'
import type { PersonMonsterType } from '@/types/monster'
import { socketEmit, socketEmitAsync } from '@/utils/web-socket'
import { dayjs } from 'element-plus'
import { ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import EndlessTowerRank from './EndlessTowerRank.vue'
import { watch } from 'vue'
import { useCommonStore } from '@/stores/modules/common'

const commonStore = useCommonStore()
const userStore = useUserStore()

defineProps<{ monster?: PersonMonsterType }>()
const emits = defineEmits(['start', 'sweep'])

function handleStartEndlessTower(isAuto = false) {
  if (userStore.endlessTowerTimes <= 0) return ElMessage.warning('本层挑战次数已耗尽')
  if (userStore.soulType > 3) return ElMessage.warning('二觉后无法挑战无尽之塔，请选择扫荡')
  const curTime = dayjs()
  const week = curTime.day()
  if (week === 6) {
    const hour = curTime.hour()
    if (hour === 23) {
      const min = curTime.minute()
      if (min >= 55) return ElMessage.warning('无尽之塔结算中，无法进入挑战')
    }
  }

  ElMessageBox.confirm(
    `<p class="affix">确定进入【无尽之塔】挑战吗？（不论成败都将<span class="shrink">消耗</span>挑战次数）${
      userStore.endlessTowerTimes < 3 ? '<br /><b>【若消耗所有挑战次数，则本周无法再进行无尽之塔挑战】</b>' : ''
    }${isAuto ? '<br />您选择了【自动】挑战，将自动挑战至失败为止' : ''}</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync<PersonMonsterType>({ type: 'startEndlessTower', data: { isAuto } })
      userStore.endlessTowerTimes--
      emits('start')
    })
    .catch(() => {})
}

const sweepLevel = ref(0)
function getSweepLevel() {
  socketEmit<number>({
    type: 'getSweepEndlessTowerLevel',
    callBack: (res) => {
      sweepLevel.value = res.data
    },
  })
}
function handleSweepEndlessTower() {
  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">扫荡</span>无尽之塔吗？（根据上周通关层数，本周您可扫荡至 <span class="grow">${
      sweepLevel.value + 1
    }</span>层）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync({ type: 'sweepEndlessTower' })
      userStore.endlessTowerTimes = 10
      userStore.endlessTowerLevel = sweepLevel.value
      ElMessage.success('扫荡成功，请在邮件处领取奖励')
      useCommonStore().getMailList()
      sweepLevel.value = 0
      emits('sweep')
    })
    .catch(() => {})
}
function handleSecondAwakenSweepEndlessTower() {
  if (userStore.endlessTowerLevel >= 50) return ElMessage.warning('已超出可扫荡的最高层级')
  ElMessageBox.confirm(`<p class="affix">确定<span class="info">扫荡</span>无尽之塔吗？</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({ type: 'secondAwakenSweepEndlessTower' })
      ElMessage.success('扫荡成功，请在邮件处领取奖励')
      userStore.endlessTowerTimes = 10
      userStore.endlessTowerLevel = 50
      useCommonStore().getMailList()
      emits('sweep')
    })
    .catch(() => {})
}
const rankShow = ref(false)

watch(
  () => commonStore.isSocketInit,
  (val) => {
    if (!val || userStore.advanceLevel !== 4) return
    getSweepLevel()
  },
)
</script>

<template>
  <div class="border-wrap endless-tower">
    <h4 class="darkGold">
      无尽之塔
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>无尽之塔说明</h4>
            <p>1. 无尽之塔<span class="info">每周日0点刷新</span>，每次刷新塔内都将被随机的怪物占领，同时怪物的各项数值也是随机的。</p>
            <p>2. 每层仅有<span class="info">10次</span>挑战机会，进入则扣除次数（无论成败），若无次数则本周无法再挑战。</p>
            <p>3. 您需要在<span class="info">3分钟</span>的时间内通过挑战，进入后切装无效，请提前根据怪物属性搭配合理的装备。</p>
            <p>4. 奖励规则：</p>
            <p style="text-indent: 2em">
              a.
              <span class="gold">金币</span>奖励：1~10层每层奖励 300,000金币，11~20层每层奖励 500,000金币，而后每层奖励 150,000金币。
            </p>
            <p style="text-indent: 2em">
              b. <span class="unique">材料</span>奖励：1~20层，将固定奖励 20~30<span class="darkGold">精造石</span>，而后每层奖励 1~15<span
                class="darkGold"
                >精造石</span
              >。
            </p>
            <p style="text-indent: 2em">
              c. <span class="antiquity">特殊</span>奖励：5层，固定奖励 1 <span class="darkGold">暗金</span>装备；10、15、20层，奖励 100<span
                class="darkGold"
                >精造石</span
              >，1<span class="relic">圣物藏宝图</span>。
            </p>
            <p style="text-indent: 2em">
              d. <span class="info">随机</span>奖励：每层都有概率触发一次随机奖励：<span class="info">2%</span>概率 1
              <span class="darkGold">暗金</span>装备，<span class="info">2%</span>概率 1<span class="grow">刻印券</span>；20层后，<span class="info"
                >10%</span
              >概率 1<span class="relic">圣物</span>。
            </p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p>
      <span style="margin-right: 15px">当前层级：{{ userStore.endlessTowerLevel + 1 }}</span>
      <span>剩余次数：{{ userStore.endlessTowerTimes }}</span>
    </p>
    <p>
      当前怪物：
      <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :teleported="false" :disabled="!monster">
        <template #reference>
          <el-button size="small" type="danger" plain>
            <span v-show="monster">{{ monster?.name }}</span>
            <span v-show="!monster">暂无战斗</span>
          </el-button>
        </template>
        <div class="monster-detail">
          <h3 style="margin: 5px 0">{{ monster?.name }}</h3>
          <p>血量：{{ monster?.hp }}</p>
          <p>防御：{{ monster?.def }}</p>
          <p>闪避率：{{ monster?.evs }}%</p>
          <p>抗爆率：{{ monster?.crtdef }}%</p>
          <template v-if="monster?.special?.length">
            <p>特性：</p>
            <p v-for="item of explainMonsterAffix(monster.special)" v-html="item"></p>
          </template>
        </div>
      </el-popover>
    </p>
    <div style="margin: 10px 0">
      <el-button type="success" plain @click="handleStartEndlessTower(false)" :disabled="userStore.advanceLevel !== 4">
        {{ userStore.advanceLevel !== 4 ? '进阶后解锁' : '进入' }}
      </el-button>
      <el-button color="#ffb3a7" :dark="commonStore.isDarkMode" plain @click="handleStartEndlessTower(true)" :disabled="!userStore.soulType"
        >自动</el-button
      >
      <el-button type="primary" plain @click="rankShow = true">排行</el-button>
      <el-button
        color="#f7c99c"
        :dark="commonStore.isDarkMode"
        plain
        @click="handleSweepEndlessTower"
        v-show="userStore.advanceLevel === 4 && sweepLevel !== 0 && userStore.soulType <= 3"
        >扫荡</el-button
      >
      <el-button color="#f7c99c" :dark="commonStore.isDarkMode" plain @click="handleSecondAwakenSweepEndlessTower" v-show="userStore.soulType > 3"
        >扫荡</el-button
      >
    </div>
  </div>

  <el-dialog v-model="rankShow" width="100%" style="max-width: 500px">
    <EndlessTowerRank />
    <template #footer>
      <p style="text-align: center">
        <el-button plain @click="rankShow = false" size="small">关闭</el-button>
      </p>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.endless-tower {
  flex-flow: column;
}
</style>
